import { useCallback, useEffect, useState } from "react";
import {
  createIncident, listIncidents, incidentTimeline, advanceIncident,
  recordDecision, recordApproval, approvalsFor,
  dispatchTasks, acknowledgeTask, tasksFor,
  recordObservation, observationsFor, storeHealth,
} from "./repository";
import { STATUS, incidentMetrics, verifyOutcome } from "./incidentMachine";

/* ──────────────────────────────────────────────────────────────────────────
 * Drives one incident through the loop.
 *
 * The demo's incidents live in a static array; the spine needs a row. Rather
 * than duplicating them up front, an incident is PROMOTED into the spine the
 * first time an officer opens command on it. That keeps the read-only
 * operating picture cheap and means the log contains only incidents someone
 * actually worked — which is also what makes the resolution-time statistics
 * meaningful rather than diluted by untouched rows.
 * ────────────────────────────────────────────────────────────────────────── */
export function useIncidentWorkflow(demoIncident, officer) {
  const [incident, setIncident] = useState(null);
  const [events, setEvents] = useState([]);
  const [decision, setDecision] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [observations, setObservations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  /* Browser storage health, not backend status. `durable` is always false
     here and the UI says so — see the note in repository.js. */
  const status = storeHealth();

  const refresh = useCallback(async (id) => {
    const [t, tk, obs] = await Promise.all([
      incidentTimeline(id), tasksFor(id), observationsFor(id),
    ]);
    setEvents(t.data || []);
    setTasks(tk.data || []);
    setObservations(obs.data || []);
  }, []);

  /* Promote the demo record into the spine, or pick up the existing row. */
  useEffect(() => {
    let cancelled = false;
    if (!demoIncident) { setIncident(null); return undefined; }

    (async () => {
      setBusy(true); setError(null);
      try {
        const { data: all } = await listIncidents();
        let row = (all || []).find((i) => i.id === demoIncident.id);

        if (!row) {
          const created = await createIncident({
            id: demoIncident.id,
            type: demoIncident.type,
            severity: demoIncident.severity,
            sector_id: demoIncident.sector,
            lng: demoIncident.coord[0],
            lat: demoIncident.coord[1],
            location_en: demoIncident.location?.en,
            location_mr: demoIncident.location?.mr,
            source: "operator",
            /* The originating record is simulated; promoting it into the log
               does not make it observed, so the trust level travels with it. */
            trust: demoIncident.provenance?.trust || "SIMULATED",
            detected_at: demoIncident.reportedAt,
          });
          row = created.data;

          /* Capture a baseline the moment command opens. Without a reading
             from before the intervention, VERIFY can never say anything —
             and the moment to take it is now, not after acting. */
          if (demoIncident.baselineDensity != null) {
            await recordObservation({
              incident_id: row.id,
              sector_id: demoIncident.sector,
              phase: "baseline",
              metric: "density",
              value: demoIncident.baselineDensity,
              unit: "p/m2",
              source: "simulation",
              trust: "SIMULATED",
            });
          }
        }

        if (cancelled) return;
        setIncident(row);
        await refresh(row.id);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => { cancelled = true; };
  }, [demoIncident, refresh]);

  /* ── Actions ────────────────────────────────────────────────────────── */

  const advance = useCallback(async (to, note) => {
    if (!incident) return;
    setBusy(true); setError(null);
    try {
      const hasApproval = approvals.some((a) => a.action === "approve");
      await advanceIncident(incident, to, { actor: officer, hasApproval, note });
      setIncident((prev) => ({ ...prev, status: to }));
      await refresh(incident.id);
    } catch (e) {
      /* Surfaced, never swallowed: an operator who clicks something that
         silently does nothing assumes the system is broken. */
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [incident, officer, approvals, refresh]);

  /* Record the recommendation AS OFFERED, before anyone acts on it. That is
     what makes "what did the system advise, and did we follow it" answerable
     afterwards. */
  const offerDecision = useCallback(async (rec) => {
    if (!incident) return null;
    setBusy(true); setError(null);
    try {
      const { data } = await recordDecision(rec, incident.id);
      setDecision(data);
      setApprovals([]);
      return data;
    } catch (e) { setError(e.message); return null; }
    finally { setBusy(false); }
  }, [incident]);

  const approve = useCallback(async (action, { modification, reason } = {}) => {
    if (!decision) return;
    setBusy(true); setError(null);
    try {
      await recordApproval({ decisionId: decision.id, officer, action, modification, reason });
      const { data } = await approvalsFor(decision.id);
      setApprovals(data || []);
      if (action === "approve" && incident?.status === STATUS.TRIAGED) {
        await advanceIncident({ ...incident, status: STATUS.TRIAGED }, STATUS.AUTHORISED, { actor: officer });
        setIncident((prev) => ({ ...prev, status: STATUS.AUTHORISED }));
        await refresh(incident.id);
      }
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }, [decision, officer, incident, refresh]);

  const dispatch = useCallback(async (agencyTasks) => {
    if (!incident) return;
    setBusy(true); setError(null);
    try {
      await dispatchTasks(incident.id, agencyTasks, decision?.id ?? null);
      await advanceIncident(
        { ...incident, status: STATUS.AUTHORISED }, STATUS.DISPATCHED,
        { actor: officer, hasApproval: approvals.some((a) => a.action === "approve") }
      );
      setIncident((prev) => ({ ...prev, status: STATUS.DISPATCHED }));
      await refresh(incident.id);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }, [incident, decision, officer, approvals, refresh]);

  const acknowledge = useCallback(async (taskId) => {
    setBusy(true); setError(null);
    try {
      await acknowledgeTask(taskId, officer);
      const { data } = await tasksFor(incident.id);
      setTasks(data || []);
      /* The incident moves to acknowledged once ANY agency responds — the
         clock that matters operationally is time-to-first-response. */
      if (incident.status === STATUS.DISPATCHED) {
        await advanceIncident(incident, STATUS.ACKNOWLEDGED, { actor: officer });
        setIncident((prev) => ({ ...prev, status: STATUS.ACKNOWLEDGED }));
        await refresh(incident.id);
      }
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }, [incident, officer, refresh]);

  const measureAfter = useCallback(async (value) => {
    if (!incident) return;
    setBusy(true); setError(null);
    try {
      await recordObservation({
        incident_id: incident.id, sector_id: incident.sector_id,
        phase: "after", metric: "density", value,
        unit: "p/m2", source: "simulation", trust: "SIMULATED",
      });
      await refresh(incident.id);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }, [incident, refresh]);

  const resolve = useCallback((note) => advance(STATUS.RESOLVED, note), [advance]);

  return {
    incident, events, decision, approvals, tasks, observations,
    busy, error, spine: status,
    metrics: incident ? incidentMetrics(incident, events) : null,
    verification: verifyOutcome(observations, "density"),
    hasApproval: approvals.some((a) => a.action === "approve"),
    advance, offerDecision, approve, dispatch, acknowledge, measureAfter, resolve,
  };
}
